import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import type { FeatureName } from "@/types";
import { getNewAppUrl, shouldSkipNewBuyerAppRedirect } from "@/utils";
import { sentryCaptureException } from "@/utils/error-handling/sentryCaptureException";
import UserService from "@/services/User";
import { useAuthStore } from "@/stores/userAuth";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

/**
 * Cross-app redirect for Merchant-of-Record users on the legacy pages
 * we've migrated to the new app:
 *
 *   - `/dashboard`            →  new-app `/` (MoR brain dashboard)
 *   - `/admin/users`          →  new-app `/admin/team`
 *   - `/orders`               →  new-app `/orders` (list)
 *   - `/orders/:orderId`      →  new-app `/orders/:orderId` (detail)
 *   - `/orders/:orderId/edit` →  new-app `/orders/:orderId/edit`
 *   - `/tasks`                →  new-app `/tasks` (list)
 *   - `/tasks/:taskUuid`      →  new-app `/tasks/:taskUuid` (detail)
 *   - `/buyers`               →  new-app `/buyers` (list)
 *   - `/buyers/:buyerUuid`    →  new-app `/buyers/:buyerUuid` (detail)
 *   - `/buyer-onboarding`     →  new-app `/buyers?tab=onboarding`
 *   - `/buyer-onboarding/:buyerUuid`
 *                             →  new-app `/buyers/onboarding/:buyerUuid`
 *
 * Mirrors `useBrandRedirect` but only fires for MoR users and only on
 * the paths above — every other MoR page (`/contracts`,
 * `/opply-documents`, etc.) still renders here. In-progress surfaces
 * are gated on their own ConfigCat flag so they roll out
 * independently: the dashboard/admin rules use
 * `isNewMorDashboardEnabled`, the order rules use
 * `isOrdersMigrationEnabled`, the buyer rules use
 * `isMorBuyersMigrationEnabled` — the same flag that gates the
 * destination page in the new app, so the two sides can never
 * disagree. Fully-released surfaces (tasks) carry no flag and always
 * redirect. Order *create* (`/orders/create`) is intentionally
 * excluded — it has not been migrated yet, and neither have the
 * deeper buyer children (`/buyers/:buyerUuid/credit-notes`,
 * `/buyers/:buyerUuid/suppliers/:supplierUuid`,
 * `/buyers/:buyerUuid/supplier-submissions/:submissionId`).
 */

const MINT_TIMEOUT_MS = 5000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error(`Timed out after ${ms}ms`)),
            ms
        );
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (err) => {
                clearTimeout(timer);
                reject(err);
            }
        );
    });

const TEMP_TOKEN_PARAM = "temp_token";

// Paths handled by the new app. Each rule matches a legacy path and
// resolves the *path-with-query* to hand off to on the new app; the temp
// token is appended to the URL fragment so it never reaches a server log
// / Referer header during the cross-app hop. A rule may carry its own
// feature flag so an in-progress surface rolls out independently; rules
// for fully-released surfaces (e.g. tasks) omit `flag` and always redirect.
interface RedirectRule {
    pattern: RegExp;
    flag?: FeatureName;
    toTarget: (path: string) => string;
}

const REDIRECT_RULES: RedirectRule[] = [
    {
        pattern: /^\/dashboard$/,
        flag: "isNewMorDashboardEnabled",
        toTarget: () => "/",
    },
    {
        pattern: /^\/admin\/users$/,
        flag: "isNewMorDashboardEnabled",
        toTarget: () => "/admin/team",
    },
    {
        pattern: /^\/orders$/,
        flag: "isOrdersMigrationEnabled",
        toTarget: () => "/orders",
    },
    // Detail — any single segment except `create` (not yet migrated).
    {
        pattern: /^\/orders\/(?!create$)[^/]+$/,
        flag: "isOrdersMigrationEnabled",
        toTarget: (path) => path,
    },
    {
        pattern: /^\/orders\/[^/]+\/edit$/,
        flag: "isOrdersMigrationEnabled",
        toTarget: (path) => path,
    },
    {
        pattern: /^\/buyers$/,
        flag: "isMorBuyersMigrationEnabled",
        toTarget: () => "/buyers",
    },
    // Detail — a single segment only. The deeper buyer children (credit
    // notes, buyer-supplier details, supplier submissions) have no
    // counterpart on the new app, so they must keep rendering here.
    {
        pattern: /^\/buyers\/[^/]+$/,
        flag: "isMorBuyersMigrationEnabled",
        toTarget: (path) => path,
    },
    // The new app folds the onboarding list into a tab on `/buyers`…
    {
        pattern: /^\/buyer-onboarding$/,
        flag: "isMorBuyersMigrationEnabled",
        toTarget: () => "/buyers?tab=onboarding",
    },
    // …and puts the onboarding detail under `/buyers/onboarding/`.
    {
        pattern: /^\/buyer-onboarding\/[^/]+$/,
        flag: "isMorBuyersMigrationEnabled",
        toTarget: (path) =>
            path.replace(/^\/buyer-onboarding\//, "/buyers/onboarding/"),
    },
    // Tasks is fully released on the new app — no flag, always redirect.
    {
        pattern: /^\/tasks$/,
        toTarget: () => "/tasks",
    },
    // Detail — any single segment task UUID.
    {
        pattern: /^\/tasks\/[^/]+$/,
        toTarget: (path) => path,
    },
];

const normalisePath = (pathname: string): string =>
    pathname.endsWith("/") && pathname !== "/"
        ? pathname.slice(0, -1)
        : pathname;

const buildTargetUrl = (
    base: string,
    newAppTarget: string,
    tempToken?: string
): string => {
    if (!tempToken) return `${base}${newAppTarget}`;
    const tokenPair = `${TEMP_TOKEN_PARAM}=${encodeURIComponent(tempToken)}`;
    return `${base}${newAppTarget}#${tokenPair}`;
};

export const useMerchantOfRecordRedirect = () => {
    const authStore = useAuthStore();
    const route = useRoute();
    const { checkFeatureAvailability } = useFeatureFlag();

    const flagResolvedDisabled = ref(false);
    const hasRedirected = ref(false);

    const isAuthenticatedMor = computed(
        () =>
            authStore.auth &&
            authStore.loaded &&
            !authStore.masqueradeMode &&
            authStore.isMerchantOfRecord
    );

    const matchedRule = computed<{
        target: string;
        flag?: FeatureName;
    } | null>(() => {
        if (route.meta.requiresAuth !== true) return null;
        const normalised = normalisePath(route.path);
        const rule = REDIRECT_RULES.find((candidate) =>
            candidate.pattern.test(normalised)
        );
        if (!rule) return null;
        return { target: rule.toTarget(normalised), flag: rule.flag };
    });

    const performRedirect = async (newAppTarget: string) => {
        const base = getNewAppUrl();
        if (!base) {
            // No destination configured — unblock the current app rather
            // than leaving the user on a blank page.
            flagResolvedDisabled.value = true;
            return;
        }

        hasRedirected.value = true;

        let tempToken: string | undefined;
        try {
            const response = await withTimeout(
                UserService.getInstance().mintSsoTemporaryToken(),
                MINT_TIMEOUT_MS
            );
            tempToken = response.data?.token;
        } catch (err) {
            // Mint failures (and timeouts) must not block the redirect —
            // the user falls back to logging in on the destination
            // instead of sitting on a blocked page.
            sentryCaptureException(err);
        }

        window.location.replace(buildTargetUrl(base, newAppTarget, tempToken));
    };

    watch(
        [matchedRule, isAuthenticatedMor],
        async ([rule, authedMor]) => {
            if (hasRedirected.value) return;
            if (!rule || !authedMor) return;

            // Engineering escape hatch — same env flag we already honour
            // for the buyer-side redirect so local QA on the legacy
            // pages is consistent across user types.
            if (shouldSkipNewBuyerAppRedirect()) {
                flagResolvedDisabled.value = true;
                return;
            }

            // Flag-less rules (released surfaces) always redirect; flagged
            // rules defer to ConfigCat.
            const isEnabled = rule.flag
                ? await checkFeatureAvailability(rule.flag)
                : true;

            // Re-check after the await: route or auth state may have
            // changed (or the redirect may have been kicked off by
            // another tick).
            if (hasRedirected.value) return;
            const currentRule = matchedRule.value;
            if (!currentRule || !isAuthenticatedMor.value) {
                return;
            }

            // The flag result belongs to the rule that started this
            // lookup. If the route has since moved to a different rule,
            // drop this run rather than applying one surface's flag to
            // another's target — the watcher has already fired again for
            // the new route and resolves its own flag.
            if (
                currentRule.target !== rule.target ||
                currentRule.flag !== rule.flag
            ) {
                return;
            }

            if (isEnabled) {
                performRedirect(currentRule.target);
            } else {
                flagResolvedDisabled.value = true;
            }
        },
        { immediate: true }
    );

    const shouldBlockRender = computed(() => {
        if (!matchedRule.value) return false;
        if (!isAuthenticatedMor.value) return false;
        return !flagResolvedDisabled.value || hasRedirected.value;
    });

    return {
        shouldBlockRender,
    };
};
